import React, { ComponentType } from 'react';
import _get from 'lodash/get';
import classNames from 'classnames';
import { IoChevronDownSharp } from 'react-icons/io5';
import { IoMdCloseCircleOutline } from 'react-icons/io';
import { Field as FormikField, useFormikContext } from 'formik';
import Error from './Error';

interface FieldProps {
  id: string;
  name: string;
  label?: string;
  type?: string;
  disabled?: boolean;
  placeholder?: string;
  helper?: string;
  clearable?: boolean;
  as?: 'select' | 'textarea';
  children?: React.ReactNode;
  onClear?: () => void;
  render?: React.ReactNode | ComponentType<any>;
}

const Field: React.FC<FieldProps> = ({
  id,
  name,
  label,
  type = 'text',
  disabled,
  placeholder,
  clearable,
  as,
  children,
  onClear,
  render,
  helper
}) => {
  const { values, errors, touched, setFieldValue } = useFormikContext();
  const fieldError = !!_get(errors, name);
  const fieldTouched = !!_get(touched, name);
  const showError = fieldError && fieldTouched;

  return (
    <div className="w-full mb-6">
      {label && <div className="text-sm font-normal text-black">{label}</div>}

      <div className="w-full relative">
        <FormikField
          id={id}
          name={name}
          type={type}
          disabled={disabled}
          className={classNames(
            'mt-1 w-full border rounded-md px-4 py-3 appearance-none',
            showError ? 'border-red-800' : 'border-gray-200',
            _get(values, name) ? 'text-gray-900' : 'text-gray-400'
          )}
          placeholder={placeholder}
          as={as}
          render={render}
        >
          {children}
        </FormikField>

        {clearable && !!_get(values, name) && (
          <div
            className="absolute top-4 right-4 cursor-pointer"
            onClick={() => {
              setFieldValue(name, '');
              onClear?.();
            }}
          >
            <IoMdCloseCircleOutline size={20} />
          </div>
        )}

        {/* Arrow icon for select field */}
        {as === 'select' && (
          <div
            className={classNames(
              'absolute top-[19px] right-4 cursor-pointer text-gray-500',
              clearable && !!_get(values, name) ? 'right-14' : 'right-4'
            )}
          >
            <IoChevronDownSharp />
          </div>
        )}
      </div>

      {showError && <Error error={_get(errors, name)} />}

      {helper && (
        <div className="text-sm font-normal pt-1 text-neutral-500">
          {helper}
        </div>
      )}
    </div>
  );
};

export default Field;
